import { useQuery } from "@tanstack/react-query";
import { container } from "@infrastructure/container";
import { useMemo } from "react";
import { getISOWeek, setISOWeekYear, setISOWeek, setISODay, getISOWeekYear } from "date-fns";

interface UseSalesChartOptions {
  startDate?: Date;
  endDate?: Date;
  productId?: string;
  showHistoricForecast?: boolean;
  marketplaces?: string[];
  countries?: string[];
}

export function useSalesChart({ startDate, endDate, showHistoricForecast = false, marketplaces, countries }: UseSalesChartOptions) {

  const getDateFromYearAndWeek = (year: number, week: number): Date => {
    let date = new Date(year, 0, 4);
    date = setISOWeekYear(date, year);
    date = setISOWeek(date, week);
    date = setISODay(date, 1);
    return date;
  }

  const {
    data: companySales,
    isLoading: isLoadingCompanySales,
    error: errorCompanySales
  } = useQuery({
    queryKey: ['companySales', startDate?.toISOString(), endDate?.toISOString(), marketplaces, countries],
    queryFn: async () => {
      const result = await container.salesRepository.getCompanySales(startDate, endDate, marketplaces, countries);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
  });

  const chartData = useMemo(() => {
    if (!companySales) {
      return [];
    }

    return companySales.map((week) => {
      
      if (!showHistoricForecast && (
        (week.year < getISOWeekYear(new Date())) ||
        (week.year === getISOWeekYear(new Date()) && week.week < getISOWeek(new Date()))
      )) {
        return {
          ...week,
          date: getDateFromYearAndWeek(week.year, week.week),
          forecastQuantity: undefined,
        };
      }


      if ((week.year > getISOWeekYear(new Date())) ||
        (week.year === getISOWeekYear(new Date()) && week.week > getISOWeek(new Date()))) {
        return {
          ...week,
          date: getDateFromYearAndWeek(week.year, week.week),
          quantity: undefined,
        };
      }

      return {
        ...week,
        date: getDateFromYearAndWeek(week.year, week.week),
      };
    });
  }, [companySales, showHistoricForecast]);

  return {
    chartData,
    isLoadingCompanySales,
    errorCompanySales,
  };
}